import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useTheme } from '../../context/ThemeContext';
import { notificationsApi } from '../../lib/api';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';
import { ScrollArea } from '../ui/scroll-area';
import { Sheet, SheetContent, SheetTrigger, SheetTitle, SheetDescription } from '../ui/sheet';
import SidebarContent from './SidebarContent';
import {
  LayoutDashboard,
  FileText,
  Users,
  Clock,
  BarChart3,
  Settings,
  LogOut,
  Bell,
  Sun,
  Moon,
  Menu,
  Shield,
  Target,
  Activity,
  ClipboardList,
  Building2,
  Flag,
  MessageSquare,
  Phone,
  Radio,
  Zap,
  ChevronRight,
  PanelLeftClose,
  PanelLeftOpen,
} from 'lucide-react';

const navItems = [
  { path: '/dashboard', label: 'Dashboard', icon: LayoutDashboard, roles: [] },

  // Simcell / Observer / Super Admin
  { path: '/exercises', label: 'Exercises', icon: Target, roles: ['super_admin', 'simcel', 'observer'] },

  // Simcell + Super Admin modules
  { path: '/injects', label: 'Injects', icon: Activity, roles: ['super_admin', 'simcel'] },
  { path: '/inject-database', label: 'Inject Database', icon: FileText, roles: ['super_admin'] },
  { path: '/objectives', label: 'Objectives', icon: Flag, roles: ['super_admin', 'simcel'] },
  { path: '/media-portal', label: 'Media Portal', icon: Radio, roles: ['super_admin', 'simcel'] },
  { path: '/reports', label: 'Reports', icon: BarChart3, roles: ['super_admin', 'simcel'] },
  { path: '/messages', label: 'Messages', icon: MessageSquare, roles: ['super_admin', 'hub_admin', 'simcel'] },
  { path: '/organisation', label: 'Organisation', icon: Building2, roles: ['super_admin'] },
  { path: '/audit-logs', label: 'Audit Logs', icon: ClipboardList, roles: ['super_admin'] },
  { path: '/users', label: 'Users', icon: Users, roles: ['super_admin', 'hub_admin'] },

  { path: '/settings', label: 'Settings', icon: Settings, roles: ['super_admin', 'simcel', 'participant', 'observer'] },
];

export const DashboardLayout = ({ children, title }) => {
  const { user, logout, hasRole, isParticipant } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const location = useLocation();
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [sidebarOpen, setSidebarOpen] = useState(false);

  // Auto-collapse sidebar when viewing exercise details / control room
  const isExerciseView = /^\/(exercises|control-room|pbx-admin)\/[^/]+/.test(location.pathname);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(isExerciseView);

  useEffect(() => {
    setSidebarCollapsed(isExerciseView);
  }, [isExerciseView]);

  const loadNotifications = async () => {
    if (isParticipant()) return; // Participants don't receive notifications
    try {
      const data = await notificationsApi.getAll();
      setNotifications(data);
      setUnreadCount(data.filter(n => !n.read).length);
    } catch (error) {
      console.error('Failed to load notifications:', error);
    }
  };

  useEffect(() => {
    const handleWsMessage = (event) => {
      if (event.detail.type === 'notification') {
        loadNotifications();
      }
    };

    window.addEventListener('ws-message', handleWsMessage);
    return () => window.removeEventListener('ws-message', handleWsMessage);
  }, []);

  useEffect(() => {
    // initial load (deferred to avoid setState-in-effect lint rule)
    const t = setTimeout(() => {
      loadNotifications();
    }, 0);
    return () => clearTimeout(t);
  }, []);

  const handleMarkAllRead = async () => {
    try {
      await notificationsApi.markAllRead();
      loadNotifications();
    } catch (error) {
      console.error('Failed to mark notifications as read:', error);
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const isSimcel = user?.roles?.includes('simcel') || user?.roles?.includes('controller');
  const filteredNavItems = navItems.filter(item => {
    if (item.roles.length === 0) return true;
    if (!hasRole(item.roles)) return false;
    if (isSimcel && (item.path === '/time-tracking' || item.path === '/reports')) return false;
    return true;
  });

  // SidebarContent moved to /src/components/SidebarContent.js to avoid re-creating components during render
  const sidebarProps = {
    user,
    items: filteredNavItems,
    pathname: location.pathname,
    onNavigate: () => setSidebarOpen(false),
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Desktop Sidebar */}
      <aside className={`hidden lg:fixed lg:inset-y-0 lg:left-0 lg:flex lg:flex-col border-r border-border bg-card transition-all duration-300 ${
        sidebarCollapsed ? 'lg:w-0 lg:overflow-hidden lg:border-r-0' : 'lg:w-64'
      }`}>
        <SidebarContent {...sidebarProps} />
      </aside>

      {/* Sidebar expand button (when collapsed) */}
      {sidebarCollapsed && (
        <button
          onClick={() => setSidebarCollapsed(false)}
          className="hidden lg:flex fixed top-20 left-0 z-50 items-center justify-center w-6 h-12 bg-card border border-l-0 border-border rounded-r-md hover:bg-accent transition-colors"
          data-testid="expand-sidebar-btn"
          title="Show sidebar"
        >
          <ChevronRight className="w-3.5 h-3.5 text-muted-foreground" />
        </button>
      )}

      {/* Mobile Sidebar */}
      <Sheet open={sidebarOpen} onOpenChange={setSidebarOpen}>
        <SheetContent side="left" className="w-64 p-0">
          <SheetTitle className="sr-only">Navigation Menu</SheetTitle>
          <SheetDescription className="sr-only">Access main navigation and settings.</SheetDescription>
          <SidebarContent {...sidebarProps} />
        </SheetContent>
      </Sheet>

      {/* Main Content */}
      <div className={`transition-all duration-300 ${sidebarCollapsed ? 'lg:pl-0' : 'lg:pl-64'}`}>
        {/* Top Header */}
        <header className="sticky top-0 z-40 border-b border-border bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
          <div className="flex h-16 items-center gap-4 px-4 md:px-6">
            {/* Mobile menu button */}
            <Button
              variant="ghost"
              size="icon"
              className="lg:hidden"
              onClick={() => setSidebarOpen(true)}
              data-testid="mobile-menu-btn"
            >
              <Menu className="w-5 h-5" />
            </Button>

            {/* Desktop sidebar toggle */}
            {!sidebarCollapsed && (
              <Button
                variant="ghost"
                size="icon"
                className="hidden lg:flex"
                onClick={() => setSidebarCollapsed(true)}
                data-testid="collapse-sidebar-btn"
                title="Hide sidebar"
              >
                <PanelLeftClose className="w-5 h-5" />
              </Button>
            )}
            {sidebarCollapsed && (
              <Button
                variant="ghost"
                size="icon"
                className="hidden lg:flex"
                onClick={() => setSidebarCollapsed(false)}
                data-testid="expand-sidebar-header-btn"
                title="Show sidebar"
              >
                <PanelLeftOpen className="w-5 h-5" />
              </Button>
            )}

            {/* Page Title */}
            <div className="flex-1">
              <h1 className="font-secondary font-bold text-xl md:text-2xl tracking-tight uppercase">
                {title}
              </h1>
            </div>

            {/* Actions */}
            <div className="flex items-center gap-2">
              {/* V14.10.3 — Global Switch-to-Media-Portal button.
                  Available for any user that has MP access. Mirrors the
                  Switch-to-EMS button in the MP sidebar. */}
              {(user?.mp_roles?.length > 0 || user?.roles?.length > 0) && (
                <Button
                  variant="outline"
                  size="sm"
                onClick={async () => {
                  // V14.10.9 — if we're on an EMS exercise page and the
                  // exercise is bridged to an MP exercise, jump straight
                  // to that linked MP exercise instead of /mp/dashboard.
                  const match = location.pathname.match(/^\/(?:exercises|control-room|pbx-admin)\/([^/?#]+)/);
                  if (match && match[1]) {
                    try {
                      const settings = await (await import('../../lib/api')).mediaPortalApi.getSettings(match[1]);
                      if (settings?.linked && settings?.mp_exercise_id) {
                        // Route is plural: `/mp/exercises/:exerciseId` (see App.js)
                        navigate(`/mp/exercises/${settings.mp_exercise_id}`);
                        return;
                      }
                    } catch { /* fall through to default */ }
                  }
                  navigate(hasRole('observer') && !hasRole('super_admin') && !hasRole('simcel') ? '/mp/exercises' : '/mp/dashboard');
                }}
                  className="border-dashed border-red-500/30 bg-red-500/5 text-red-500 hover:bg-red-500/10 hover:text-red-600"
                  data-testid="ems-switch-to-mp"
                  title="Switch to Media Portal"
                >
                  <Radio className="w-3.5 h-3.5 mr-1.5" />
                  Switch to MP
                </Button>
              )}

              {/* Theme Toggle */}
              <Button
                variant="ghost"
                size="icon"
                onClick={toggleTheme}
                data-testid="theme-toggle"
              >
                {theme === 'dark' ? (
                  <Sun className="w-5 h-5" />
                ) : (
                  <Moon className="w-5 h-5" />
                )}
              </Button>

              {/* Notifications — hidden for participants */}
              {!isParticipant() && (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" size="icon" className="relative" data-testid="notifications-btn">
                    <Bell className="w-5 h-5" />
                    {unreadCount > 0 && (
                      <Badge 
                        className="absolute -top-1 -right-1 h-5 w-5 flex items-center justify-center p-0 text-xs"
                        variant="destructive"
                      >
                        {unreadCount > 9 ? '9+' : unreadCount}
                      </Badge>
                    )}
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-80">
                  <div className="flex items-center justify-between px-4 py-2 border-b">
                    <span className="font-semibold">Notifications</span>
                    {unreadCount > 0 && (
                      <Button variant="ghost" size="sm" onClick={handleMarkAllRead}>
                        Mark all read
                      </Button>
                    )}
                  </div>
                  <ScrollArea className="h-[300px]">
                    {notifications.length === 0 ? (
                      <div className="p-4 text-center text-muted-foreground">
                        No notifications
                      </div>
                    ) : (
                      notifications.slice(0, 10).map((notif) => (
                        <DropdownMenuItem
                          key={notif.notification_id}
                          className={`flex flex-col items-start gap-1 p-4 cursor-pointer ${
                            !notif.read ? 'bg-primary/5' : ''
                          }`}
                        >
                          <span className="font-medium">{notif.title}</span>
                          <span className="text-sm text-muted-foreground line-clamp-2">
                            {notif.message}
                          </span>
                          <span className="text-xs text-muted-foreground">
                            {new Date(notif.created_at).toLocaleString()}
                          </span>
                        </DropdownMenuItem>
                      ))
                    )}
                  </ScrollArea>
                </DropdownMenuContent>
              </DropdownMenu>
              )}

              {/* User Menu */}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" size="icon" className="rounded-full" data-testid="user-menu-btn">
                    <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center">
                      {user?.picture ? (
                        <img src={user.picture} alt="" className="w-8 h-8 rounded-full" />
                      ) : (
                        <span className="font-semibold text-sm text-primary">
                          {user?.name?.charAt(0).toUpperCase()}
                        </span>
                      )}
                    </div>
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56">
                  <div className="px-4 py-3 border-b">
                    <p className="font-medium">{user?.name}</p>
                    <p className="text-sm text-muted-foreground">{user?.email}</p>
                  </div>
                  <DropdownMenuItem asChild>
                    <Link to="/settings" className="cursor-pointer">
                      <Settings className="w-4 h-4 mr-2" />
                      Settings
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={handleLogout} className="text-destructive cursor-pointer" data-testid="logout-btn">
                    <LogOut className="w-4 h-4 mr-2" />
                    Logout
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
        </header>

        {/* Page Content */}
        <main className="p-4 md:p-6 lg:p-8 grid-background min-h-[calc(100vh-4rem)]">
          <div className="animate-slide-up">
            {children}
          </div>
        </main>
      </div>
    </div>
  );
};

export default DashboardLayout;
